import { createApi } from '@reduxjs/toolkit/dist/query/react'

import { FeatureType } from 'clickNgo/configs/feature'
import { axiosProcedureQuery } from 'clickNgo/helpers/api'

import { getKeyInitiativesTagId } from '../helpers/getKeyInitiativesTagId'
import { normalizeKeyInitiativeStatuses } from '../model/status'
import type {
  GetGPKeyInitiativesRollupParams,
  GetKeyInitiativesParams,
  KeyInitiative,
} from '../model/types'

export const KEY_INITIATIVES_TAG = 'KeyInitiatives'

export const buildGetKeyInitiativesRequestData = ({
  partnerId,
  fYear,
  programType,
}: GetKeyInitiativesParams) => {
  const normalizedProgramType = programType?.trim()

  return {
    'p.PartnerId': partnerId,
    'p.FYear': fYear,
    ...(normalizedProgramType ? { 'p.ProgramType': normalizedProgramType } : {}),
  }
}

export const buildGetGPKeyInitiativesRollupRequestData = ({
  globalPartnerId,
  fYear,
  programType,
}: GetGPKeyInitiativesRollupParams) => {
  const normalizedProgramType = programType?.trim()

  return {
    'p.GlobalPartnerId': globalPartnerId,
    'p.FYear': fYear,
    ...(normalizedProgramType ? { 'p.ProgramType': normalizedProgramType } : {}),
  }
}

export const keyInitiativesApi = createApi({
  reducerPath: `${FeatureType.P360Api}/keyInitiativesApi`,
  baseQuery: axiosProcedureQuery<KeyInitiative[]>(),
  tagTypes: [KEY_INITIATIVES_TAG],
  endpoints(build) {
    return {
      getKeyInitiatives: build.query<KeyInitiative[], GetKeyInitiativesParams>({
        providesTags: (_result, _error, arg) => [
          {
            type: KEY_INITIATIVES_TAG,
            id: getKeyInitiativesTagId(arg),
          },
        ],
        transformResponse(response: { Data?: KeyInitiative[] } | undefined) {
          const records = response?.Data
          return Array.isArray(records) ? normalizeKeyInitiativeStatuses(records) : []
        },
        query(params) {
          return {
            url: '/webservice/197/sp/v4/spJBPKeyInitiatives/call',
            method: 'POST',
            data: buildGetKeyInitiativesRequestData(params),
          }
        },
      }),

      getGPKeyInitiativesRollup: build.query<KeyInitiative[], GetGPKeyInitiativesRollupParams>({
        providesTags: (_result, _error, arg) => [
          {
            type: KEY_INITIATIVES_TAG,
            id: `gp-rollup::${arg.globalPartnerId}::${arg.fYear}::${arg.programType ?? ''}`,
          },
        ],
        transformResponse(response: { Data?: KeyInitiative[] } | undefined) {
          const records = response?.Data
          return Array.isArray(records) ? normalizeKeyInitiativeStatuses(records) : []
        },
        query(params) {
          return {
            url: '/webservice/197/sp/v4/spJBPGPKeyInitiativesRollup/call',
            method: 'POST',
            data: buildGetGPKeyInitiativesRollupRequestData(params),
          }
        },
      }),
    }
  },
})

export const { useGetKeyInitiativesQuery, useGetGPKeyInitiativesRollupQuery } = keyInitiativesApi
